import {
  ASK_HELENA_STREAM_TIMEOUT_MS,
  useAskHelenaStream,
  useRefreshAskHelenaConversations,
  useRefreshAskHelenaConversationTitle,
  useStopAskHelenaTurn,
} from "controller";
import { AskHelenaHttpError } from "controller/api/helena/askHelena";
import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import type {
  AskHelenaAbortReason,
  AskHelenaPendingWriteStatus,
  AskHelenaSseEvent,
} from "shared";
import { getApiErrorMessage } from "utilz/helpers";
import { v4 as uuidv4 } from "uuid";
import {
  type AiFailureType,
  type AiQueryTrigger,
  hasMarkdownTable,
  recordQuery,
  recordQueryOutcome,
  scrubQueryText,
  trackAiHome,
} from "../../analytics";
import {
  appendAskHelenaToken,
  askHelenaConversationId,
  failAskHelenaTurn,
  finishAskHelenaTool,
  finishAskHelenaTurn,
  isAskHelenaBusy,
  isAskHelenaStreaming,
  markAskHelenaTurnInterrupted,
  resolveAskHelenaPendingWrite,
  resumeAskHelenaTurn,
  setAskHelenaConversationId,
  setAskHelenaMessageId,
  startAskHelenaTool,
  startAskHelenaTurn,
  stopAskHelenaTurn,
  suspendAskHelenaTurn,
} from "../store";

/**
 * What the user is told when a proposal can no longer be acted on. The service
 * answers 404 to every kind of "no" here — already confirmed, already
 * cancelled, expired, someone else's, never existed — deliberately
 * indistinguishable, so this copy covers all five without guessing.
 */
const PENDING_WRITE_GONE = "That action is no longer available.";
const GONE_STATUS = 404;

const isPendingWriteGone = (error: unknown): boolean =>
  error instanceof AskHelenaHttpError && error.status === GONE_STATUS;

const toFailureType = (error: unknown): AiFailureType =>
  error instanceof AskHelenaHttpError ? `http_${error.status}` : "error";

interface TurnAnalytics {
  queryId: string;
  startedAt: number;
  firstTokenAt: number | null;
  chatId: string | null;
  queryText: string;
  queryTextScrubbed: boolean;
  answer: string;
  toolCount: number;
  awaitingConfirmation: boolean;
  stopped: boolean;
  interrupted: boolean;
  reported: boolean;
}

const recordTurnEvent = (
  turn: TurnAnalytics,
  event: AskHelenaSseEvent,
): void => {
  switch (event.type) {
    case "conversation":
      turn.chatId = event.conversationId;
      break;
    case "tool_call":
      turn.toolCount += 1;
      break;
    case "token":
      turn.firstTokenAt ??= Date.now();
      turn.answer += event.text;
      break;
    case "awaiting_confirmation":
      turn.awaitingConfirmation = true;
      break;
  }
};

interface UseAskHelenaReturn {
  send: (
    question: string,
    trigger: AiQueryTrigger,
    options?: { voiceSessionId?: string },
  ) => Promise<void>;
  /** Approves a proposed change and streams the rest of the turn. */
  confirm: (pendingWriteId: string) => Promise<void>;
  /** Declines it. */
  cancel: (pendingWriteId: string) => Promise<void>;
  stop: () => void;
  isBusy: boolean;
  isStreaming: boolean;
}

/**
 * Wires the SSE transport onto the Jotai state machine. Only `token` events
 * produce answer text — `conversation`, `tool_call` and `tool_result` exist to
 * drive the thinking state, and `awaiting_confirmation` suspends the turn.
 */
export const useAskHelena = (): UseAskHelenaReturn => {
  const isBusy = useAtomValue(isAskHelenaBusy);
  const isStreaming = useAtomValue(isAskHelenaStreaming);
  const conversationId = useAtomValue(askHelenaConversationId);

  const startTurn = useSetAtom(startAskHelenaTurn);
  const setConversationId = useSetAtom(setAskHelenaConversationId);
  const setMessageId = useSetAtom(setAskHelenaMessageId);
  const appendToken = useSetAtom(appendAskHelenaToken);
  const startTool = useSetAtom(startAskHelenaTool);
  const finishTool = useSetAtom(finishAskHelenaTool);
  const failTurn = useSetAtom(failAskHelenaTurn);
  const finishTurn = useSetAtom(finishAskHelenaTurn);
  const suspendTurn = useSetAtom(suspendAskHelenaTurn);
  const resumeTurn = useSetAtom(resumeAskHelenaTurn);
  const stopTurn = useSetAtom(stopAskHelenaTurn);
  const markTurnInterrupted = useSetAtom(markAskHelenaTurnInterrupted);
  const resolvePendingWrite = useSetAtom(resolveAskHelenaPendingWrite);

  const turn = useRef<TurnAnalytics | null>(null);
  const streamSession = useRef<string | null>(null);
  const liveConversationId = useRef<string | null>(null);
  const isStopPending = useRef(false);
  const flushStopRef = useRef<((conversationId: string) => void) | null>(null);

  const requestStop = useStopAskHelenaTurn();

  const reportTurn = useCallback((failure?: AiFailureType) => {
    const current = turn.current;
    if (!current || current.reported) return;

    current.reported = true;
    const responseTimeMs = Date.now() - current.startedAt;
    recordQueryOutcome({
      queryId: current.queryId,
      failed: Boolean(failure),
    });

    if (failure) {
      trackAiHome("ai_response_failed", {
        chat_id: current.chatId,
        query_id: current.queryId,
        query_text: current.queryText,
        query_text_scrubbed: current.queryTextScrubbed,
        failure_type: failure,
        response_time_ms: responseTimeMs,
      });
      return;
    }

    const hasTable = hasMarkdownTable(current.answer);
    trackAiHome("ai_response_received", {
      chat_id: current.chatId,
      query_id: current.queryId,
      response_type: current.awaitingConfirmation
        ? "confirmation"
        : hasTable
          ? "table"
          : "text",
      response_time_ms: responseTimeMs,
      time_to_first_token_ms: current.firstTokenAt
        ? current.firstTokenAt - current.startedAt
        : null,
      answer_length: current.answer.length,
      tool_count: current.toolCount,
      has_table: hasTable,
    });
  }, []);

  const settleTurn = useCallback(() => {
    const current = turn.current;
    if (!current || current.reported) return;

    if (current.stopped) {
      reportTurn("user_stopped");
      return;
    }

    if (current.interrupted) {
      reportTurn("navigated_away");
      return;
    }

    if (Date.now() - current.startedAt >= ASK_HELENA_STREAM_TIMEOUT_MS) {
      reportTurn("timeout");
      return;
    }

    if (current.firstTokenAt === null && !current.awaitingConfirmation) {
      reportTurn("stream_incomplete");
      return;
    }

    reportTurn();
  }, [reportTurn]);

  const takeStopGate = useCallback(
    (event: AskHelenaSseEvent, sessionId: string): boolean => {
      if (event.type === "conversation") {
        liveConversationId.current = event.conversationId;
        setConversationId({ sessionId, conversationId: event.conversationId });
        if (isStopPending.current) flushStopRef.current?.(event.conversationId);
        return true;
      }
      return isStopPending.current;
    },
    [setConversationId],
  );

  const handleEvent = useCallback(
    (event: AskHelenaSseEvent) => {
      const sessionId = streamSession.current;
      if (!sessionId) return;
      if (turn.current) recordTurnEvent(turn.current, event);
      if (takeStopGate(event, sessionId)) return;

      switch (event.type) {
        case "tool_call":
          // `label` is the human phrase; `name` is the registry identifier and
          // must never reach the DOM.
          startTool({ sessionId, label: event.label });
          break;
        case "tool_result":
          // `ok` is ignored on purpose — a denial is explained in the answer.
          finishTool(sessionId);
          break;
        case "token":
          appendToken({ sessionId, text: event.text });
          break;
        case "awaiting_confirmation":
          // `name` is deliberately dropped here rather than carried into state
          // it could be rendered from.
          suspendTurn({
            sessionId,
            pendingWrite: {
              pendingWriteId: event.pendingWriteId,
              label: event.label,
              summary: event.summary,
              arguments: event.arguments,
            },
          });
          break;
        case "error":
          reportTurn("error");
          failTurn({ sessionId, message: event.message });
          break;
        case "done":
          if (typeof event.messageId === "string" && event.messageId) {
            setMessageId({
              sessionId,
              messageId: event.messageId,
              ticketRefs: event.ticketRefs,
            });
          }
          reportTurn();
          finishTurn(sessionId);
          break;
      }
    },
    [
      takeStopGate,
      setMessageId,
      startTool,
      finishTool,
      appendToken,
      suspendTurn,
      failTurn,
      finishTurn,
      reportTurn,
    ],
  );

  const handleStreamEnd = useCallback(
    (reason: AskHelenaAbortReason) => {
      if (reason !== "teardown") return;

      const sessionId = streamSession.current;
      if (!sessionId) return;
      if (!markTurnInterrupted(sessionId)) return;

      const current = turn.current;
      if (current) current.interrupted = true;

      trackAiHome("ai_turn_interrupted", {
        chat_id: current?.chatId ?? null,
        query_id: current?.queryId ?? null,
        elapsed_ms: current ? Date.now() - current.startedAt : 0,
        had_conversation_id: Boolean(current?.chatId),
        answer_length: current?.answer.length ?? 0,
      });
    },
    [markTurnInterrupted],
  );

  const {
    ask,
    confirm: confirmStream,
    cancel: cancelStream,
    abort,
  } = useAskHelenaStream(handleEvent, handleStreamEnd);
  const refreshConversations = useRefreshAskHelenaConversations();
  const refreshConversationTitle = useRefreshAskHelenaConversationTitle();

  const flushStop = useCallback(
    (targetConversationId: string) => {
      isStopPending.current = false;
      void requestStop(targetConversationId);
      abort("user_stop");
    },
    [abort, requestStop],
  );

  useEffect(() => {
    flushStopRef.current = flushStop;
  }, [flushStop]);

  const send = useCallback(
    async (
      question: string,
      trigger: AiQueryTrigger,
      { voiceSessionId }: { voiceSessionId?: string } = {},
    ) => {
      const trimmed = question.trim();
      if (!trimmed || isBusy) return;

      const userMessageId = uuidv4();
      const submittedAt = Date.now();
      const chatId = conversationId ?? null;
      const scrubbed = scrubQueryText(trimmed);
      const { queryIndex, isFirstQuery } = recordQuery(userMessageId);

      turn.current = {
        queryId: userMessageId,
        startedAt: submittedAt,
        firstTokenAt: null,
        chatId,
        queryText: scrubbed.text,
        queryTextScrubbed: scrubbed.wasScrubbed,
        answer: "",
        toolCount: 0,
        awaitingConfirmation: false,
        stopped: false,
        interrupted: false,
        reported: false,
      };

      trackAiHome("ai_query_submitted", {
        chat_id: chatId,
        query_id: userMessageId,
        query_text: scrubbed.text,
        query_text_scrubbed: scrubbed.wasScrubbed,
        query_length: trimmed.length,
        query_index: queryIndex,
        is_first_query: isFirstQuery,
        is_new_chat: chatId === null,
        trigger,
      });

      const sessionId = startTurn({
        userMessageId,
        assistantMessageId: uuidv4(),
        question: trimmed,
      });
      streamSession.current = sessionId;
      liveConversationId.current = chatId;
      isStopPending.current = false;

      try {
        await ask({
          question: trimmed,
          conversationId: conversationId ?? undefined,
          voiceSessionId,
        });
      } catch (error: unknown) {
        reportTurn(toFailureType(error));
        failTurn({
          sessionId,
          message: getApiErrorMessage(error, "Helena couldn't answer that."),
        });
      } finally {
        isStopPending.current = false;
        // The stream may end without a `done` frame (network drop, timeout);
        // this settles the status either way and preserves any error.
        settleTurn();
        finishTurn(sessionId);
        // A first question creates the conversation, so the sidebar list the
        // user is looking at no longer matches the service.
        refreshConversations();
        // The title is written a second or two later, by a call the service
        // fires after `done` and does not await — so the refetch above is
        // always too early to see it.
        if (!turn.current?.awaitingConfirmation) {
          void refreshConversationTitle(turn.current?.chatId ?? conversationId);
        }
      }
    },
    [
      ask,
      conversationId,
      failTurn,
      finishTurn,
      isBusy,
      refreshConversations,
      refreshConversationTitle,
      reportTurn,
      settleTurn,
      startTurn,
    ],
  );

  /**
   * Approve or decline, sharing everything but the endpoint.
   *
   * There is deliberately NO retry. A confirm that fails partway may already
   * have applied the change, so replaying it risks doing it twice — the one
   * outcome the whole approval flow exists to prevent. On any failure the card
   * is retired and the user is told plainly.
   */
  const resolve = useCallback(
    async (
      pendingWriteId: string,
      status: AskHelenaPendingWriteStatus,
      openStream: (pendingWriteId: string) => Promise<void>,
      failureMessage: string,
    ) => {
      // Retire the card first: it must stop being clickable the moment the
      // request is in flight, and the resumed turn streams into the message it
      // was attached to.
      const sessionId = resumeTurn({ pendingWriteId, status });
      streamSession.current = sessionId;
      isStopPending.current = false;

      try {
        await openStream(pendingWriteId);
      } catch (error: unknown) {
        const isGone = isPendingWriteGone(error);
        if (isGone) {
          resolvePendingWrite({
            sessionId,
            pendingWriteId,
            status: "expired",
          });
        }
        failTurn({
          sessionId,
          message: isGone
            ? PENDING_WRITE_GONE
            : getApiErrorMessage(error, failureMessage),
        });
      } finally {
        isStopPending.current = false;
        finishTurn(sessionId);
        resolvePendingWrite({ sessionId, pendingWriteId });
        // A turn that suspended for approval reached `done` with no answer to
        // name the thread from; the title is written when the resumed turn ends.
        void refreshConversationTitle(turn.current?.chatId ?? conversationId);
      }
    },
    [
      conversationId,
      failTurn,
      finishTurn,
      refreshConversationTitle,
      resolvePendingWrite,
      resumeTurn,
    ],
  );

  const confirm = useCallback(
    (pendingWriteId: string) =>
      resolve(
        pendingWriteId,
        "confirmed",
        confirmStream,
        // Honest, because the change may already have landed before the failure.
        "Something went wrong finishing that. Check before trying again.",
      ),
    [confirmStream, resolve],
  );

  const cancel = useCallback(
    (pendingWriteId: string) =>
      resolve(
        pendingWriteId,
        "cancelled",
        cancelStream,
        "Couldn't decline that action.",
      ),
    [cancelStream, resolve],
  );

  const stop = useCallback(() => {
    if (!isStreaming) return;

    const current = turn.current;
    if (current) current.stopped = true;

    stopTurn();

    const activeConversationId = liveConversationId.current ?? conversationId;
    if (activeConversationId) {
      flushStop(activeConversationId);
      return;
    }
    isStopPending.current = true;
  }, [conversationId, flushStop, isStreaming, stopTurn]);

  return { send, confirm, cancel, stop, isBusy, isStreaming };
};
